import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Phone, 
  CheckCircle2,
  HelpCircle,
  ChevronDown
} from 'lucide-react';
import { faqs } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Pricing = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section 
        className="py-20"
        style={{ background: 'var(--gradient-hero-warm)' }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <motion.h1
              className="heading-hero mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              How Pricing Works
            </motion.h1>
            <motion.p
              className="body-large mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Simple, transparent, and fair. You pay only for the help you receive — 
              no subscriptions, no hidden fees, no surprises.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Pricing Explanation */}
      <section className="py-20 bg-[#FFF9F2]">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Pay As You Use Card */}
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-full bg-[#E4EDF8] flex items-center justify-center mb-6">
                <CheckCircle2 className="w-7 h-7 text-[#232323]" />
              </div>
              <h3 className="heading-2 mb-4">Pay-As-You-Use</h3>
              <p className="body-medium text-[#353535] leading-relaxed">
                There are no monthly subscriptions or packages. You only pay when you 
                actually use our services. Request help once or regularly — it's entirely up to you.
              </p>
            </motion.div>

            {/* No Commitments Card */}
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 rounded-full bg-[#b8d1ba] flex items-center justify-center mb-6">
                <CheckCircle2 className="w-7 h-7 text-[#232323]" />
              </div>
              <h3 className="heading-2 mb-4">No Long-Term Commitments</h3>
              <p className="body-medium text-[#353535] leading-relaxed">
                You're not locked into any contract. Use our services when you need them, 
                and there's no obligation to continue if you don't want to.
              </p>
            </motion.div>
          </div>

          {/* How Cost is Determined */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-sm mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="heading-2 mb-6">What Affects the Cost?</h3>
            <p className="body-large mb-6">
              The cost for each service depends on a few factors:
            </p>
            <ul className="space-y-4 mb-8">
              {[
                {
                  title: 'Type of Help',
                  description: 'A simple errand costs less than accompanying someone to a hospital.'
                },
                {
                  title: 'Time Involved',
                  description: 'A quick 30-minute task is priced differently than a full-day accompaniment.'
                },
                {
                  title: 'Location',
                  description: 'Costs may vary slightly based on your city and travel requirements.'
                }
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FFF9F2] flex items-center justify-center flex-shrink-0">
                    <span className="font-mono text-sm font-medium">{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-[#232323]">{item.title}</p>
                    <p className="text-[#666] text-sm">{item.description}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="bg-[#FEEFDC] rounded-xl p-6">
              <p className="text-[#232323] font-medium">
                We always share the expected cost with you before we start. 
                No service begins without your confirmation.
              </p>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="w-6 h-6 text-[#666]" />
              <h3 className="heading-2">Common Questions</h3>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border-none shadow-sm overflow-hidden"
                >
                  <AccordionTrigger className="px-6 py-5 text-left hover:no-underline hover:bg-[#FFF9F2] transition-colors">
                    <span className="font-medium text-[#232323] text-lg pr-4">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-5 text-[#353535] leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <motion.h2
            className="heading-1 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Get a Quote?
          </motion.h2>
          <motion.p
            className="body-large mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Tell us what help you need and we'll share the expected cost — 
            no obligation required.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/request-help" className="btn-primary gap-2 inline-flex">
              <Phone className="w-4 h-4" />
              Request Help & Get Quote
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;